import { useMemo } from 'react';
import { useAvailabilityQuery, useBookingsQuery } from '../../queries/scheduling';
import { addDays, dateIn, startOfDayISO, timeIn } from './time';

const WINDOW_DAYS = 7;

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const todaySub = (next, done, tz) => {
  if (next) return `Next at ${timeIn(next.start, tz)}`;
  return done ? `${done} done, nothing left` : 'Nothing left today';
};

export function useScheduleTiles({ tz, today, enabled }) {
  const range = useMemo(
    () => ({ from: startOfDayISO(today, tz), to: startOfDayISO(addDays(today, WINDOW_DAYS), tz) }),
    [today, tz]
  );
  const bookingsQuery = useBookingsQuery(range);
  const availabilityQuery = useAvailabilityQuery({ from: today, days: WINDOW_DAYS }, { enabled });

  const tiles = useMemo(() => {
    const all = bookingsQuery.data || [];
    const upcoming = all.filter((b) => b.status === 'booked');
    const tours = upcoming.filter((b) => b.type !== 'appointment').length;
    const todays = all.filter((b) => b.status !== 'cancelled' && dateIn(b.start, tz) === today);
    const nextToday = todays.find((b) => b.status === 'booked' && new Date(b.start) > new Date());
    const doneToday = todays.filter((b) => b.status === 'completed').length;
    const unassigned = upcoming.filter((b) => !b.assignedTo).length;
    const openSlots = (availabilityQuery.data || []).reduce((n, d) => n + d.slots.length, 0);

    return [
      {
        key: 'upcoming',
        label: 'Next 7 days',
        value: upcoming.length,
        sub: `${plural(tours, 'tour')} · ${plural(upcoming.length - tours, 'appointment')}`
      },
      {
        key: 'today',
        label: 'Today',
        value: todays.length,
        sub: todaySub(nextToday, doneToday, tz)
      },
      {
        key: 'open',
        label: 'Open slots',
        value: enabled ? openSlots : 'Off',
        sub: enabled ? 'Bookable in the next 7 days' : 'Assistant booking is off',
        loading: enabled && availabilityQuery.isLoading
      },
      {
        key: 'unassigned',
        label: 'Unassigned',
        value: unassigned,
        sub: unassigned ? 'Need an agent' : 'Every booking has an agent',
        alert: unassigned > 0
      }
    ];
  }, [bookingsQuery.data, availabilityQuery.data, availabilityQuery.isLoading, enabled, today, tz]);

  return { tiles, isLoading: bookingsQuery.isLoading };
}
